import { Alert, Box, Link, Stack, TextField, Typography } from "@mui/material";
import {
  CONDITION_WINDOW_SOURCE_HINT,
  type ConditionWindowDefaults,
  type ConditionWindows,
} from "@/utils/helpers/conditionWindowDefaults";
import { conditionWindowsOutsideDefaultsWarning } from "@/utils/validations/laseredConditionValidation";

interface ConditionWindowFieldsProps {
  value: ConditionWindows;
  onChange: (next: ConditionWindows) => void;
  /** Policy defaults: prefill source, Reset target and the non-blocking range check. */
  defaults: ConditionWindowDefaults;
  disabled?: boolean;
  /** Blocking error for this row, shown under the dates. */
  error?: string;
  /** Prefix for input ids/test ids when several rows render. */
  idPrefix?: string;
  /**
   * The caller shows the outside-policy-dates note itself (once for several
   * rows, as LaseredConditionsEditor does), so do not repeat it per row.
   */
  hideRangeWarning?: boolean;
}

const FIELDS: { key: keyof ConditionWindows; label: string }[] = [
  { key: "incurred_start_date", label: "Incurred Start" },
  { key: "incurred_end_date", label: "Incurred End" },
  { key: "paid_start_date", label: "Paid Start" },
  { key: "paid_end_date", label: "Paid End" },
];

/**
 * Incurred and paid windows for one conditional laser (#7344). One component for
 * every entry point (laser dialog, claimant create, policy create, Deductibles
 * tab) so prefill, reset and validation behave the same everywhere.
 *
 * A qualifying line must have its date of service inside the incurred window AND
 * its paid date inside the paid window (inclusive), like a contract laser.
 */
export const ConditionWindowFields = ({
  value,
  onChange,
  defaults,
  disabled,
  error,
  idPrefix = "condition",
  hideRangeWarning,
}: ConditionWindowFieldsProps) => {
  const warning = hideRangeWarning ? "" : conditionWindowsOutsideDefaultsWarning(value, defaults.windows);
  const atDefaults = FIELDS.every(({ key }) => value[key] === defaults.windows[key]);

  const field = ({ key, label }: (typeof FIELDS)[number]) => (
    <TextField
      key={key}
      id={`${idPrefix}-${key}`}
      label={label}
      type="date"
      required
      fullWidth
      size="small"
      disabled={disabled}
      value={value[key]}
      onChange={(e) => onChange({ ...value, [key]: e.target.value })}
      error={!!error && !value[key]}
      slotProps={{ inputLabel: { shrink: true }, htmlInput: { "data-testid": `${idPrefix}-${key}` } }}
    />
  );

  return (
    <Box>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={2}>
          {field(FIELDS[0])}
          {field(FIELDS[1])}
        </Stack>
        <Stack direction="row" spacing={2}>
          {field(FIELDS[2])}
          {field(FIELDS[3])}
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary" data-testid={`${idPrefix}-window-hint`}>
          {CONDITION_WINDOW_SOURCE_HINT[defaults.source]}
        </Typography>
        {!disabled && !atDefaults && defaults.source !== "none" && (
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => onChange({ ...defaults.windows })}
            data-testid={`${idPrefix}-window-reset`}
          >
            Reset to policy dates
          </Link>
        )}
      </Stack>
      {error && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: "block", mt: 0.5 }}>
          {error}
        </Typography>
      )}
      {!error && warning && (
        <Alert severity="info" variant="outlined" sx={{ mt: 1, fontSize: "0.8125rem" }}>
          {warning}
        </Alert>
      )}
    </Box>
  );
};
